// src/Product/billings.js
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./billings.css";
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Check,
  RefreshCcw,
  ArrowRight,
  Building2,
  Phone,
  Mail,
  Receipt,
} from "lucide-react";

// 🔹 Static plan config (moved outside component to satisfy ESLint)
// ✅ Updated to NEW plan structure: Free, Starter, SME, Business
// ✅ Added quarterly cycle (no discount); SME has NO monthly option
const PLAN_CONFIG = {
  free: {
    name: "Free",
    monthly: 0,
    quarterly: 0,
    yearly: 0,
    perks: [
      "Basic invoicing (create simple invoices)",
      "Basic receipts (issue receipts for payments)",
      "View your saved invoices & receipts",
      "Basic dashboard summary",
      "Community support",
      "1 team seat",
      "Community access",
    ],
  },
  starter: {
    name: "Starter",
    monthly: 3000,
    quarterly: 9000, // 3 * monthly (no discount)
    yearly: 28800, // existing pricing kept
    perks: [
      "Everything in Free",
      "Create unlimited invoices & receipts (starter level)",
      "Create estimates (quotations) for customers",
      "Branded PDF (logo + business name)",
      "Basic customer list (save customers)",
      "Quick sales entry (record sales fast)",
      "Simple cash summary",
      "1 team seat",
      "Chat/Email support",
    ],
  },
  sme: {
    name: "SME",
    monthly: null, // ✅ no monthly payment for SME
    quarterly: 37500, // ✅ 12,500 * 3 (no discount)
    yearly: 150000, // ✅ 12,500 * 12 (no discount for yearly here unless you want later)
    perks: [
      "Everything in Starter",
      "Inventory management (track items & stock)",
      "Record purchases (buying) and sales (selling)",
      "Create invoices, receipts and estimates",
      "Track revenue and profit (basic reports)",
      "Sales history (see all sales records)",
      "Purchase history (see all purchase records)",
      "Vendor list (save suppliers/vendors)",
      "Stock movements (see stock in/out)",
      "Stock on hand (see available stock)",
      "2 team seats (recommended)",
      "Standard support",
    ],
  },
  business: {
    name: "Business",
    monthly: 29999,
    quarterly: 89997, // 3 * monthly (no discount)
    yearly: 287990, // existing pricing kept
    perks: [
      "Everything in SME",
      "Advanced accounting reports",
      "Management report (business summary pack)",
      "Bank reconciliation",
      "General ledger & trial balance",
      "Fixed assets register",
      "Departments/Projects",
      "5 team seats",
      "SLA Support",
      "Dedicated Support",
    ],
  },
};

const BillingPage = () => {
  const {
    companyId,
    plan: currentPlan,
    isOnTrial,
    hasUsedTrial,
    refreshCompanyContext,
  } = useCompany();

  const [billingInfo, setBillingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [cardDetails, setCardDetails] = useState({
    firstName: "",
    lastName: "",
    address: "",
    email: "",
    phone: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("free");

  // ✅ monthly | quarterly | yearly
  const [billingCycle, setBillingCycle] = useState("monthly");

  const [userId, setUserId] = useState(null);
  const [showExpiryPopup, setShowExpiryPopup] = useState(false);

  const planConfig = PLAN_CONFIG;

  // 🔹 Trial eligibility: first-time Business users only
  const trialEligible = currentPlan !== "business" && !hasUsedTrial;

  // Load current userId (for metadata) and prefill email
  useEffect(() => {
    supabase.auth.getUser().then(({ data, error }) => {
      if (!error && data?.user) {
        setUserId(data.user.id);
        if (!cardDetails.email && data.user.email) {
          setCardDetails((prev) => ({ ...prev, email: data.user.email }));
        }
      } else {
        console.warn(
          "BillingPage: user not signed in or session missing",
          error
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1) Fetch latest billing row for this company (or fallback to free)
  useEffect(() => {
    const fetchBillingInfo = async () => {
      setLoading(true);
      setError(null);

      if (!companyId) {
        setError("No company selected.");
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("billing")
        .select("*")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error fetching billing info:", error);
        setError("Error fetching billing info.");
        setLoading(false);
        return;
      }

      if (data) {
        setBillingInfo(data);

        // Normalize older values (basic/premium/pro -> starter/sme)
        const rawType = (data.billing_type || "free").toLowerCase();
        let mappedPlan = "free";
        if (rawType === "basic") mappedPlan = "starter";
        else if (rawType === "premium") mappedPlan = "sme";
        else if (rawType === "pro") mappedPlan = "sme";
        else if (rawType === "business") mappedPlan = "business";
        else if (["free", "starter", "sme", "business"].includes(rawType)) {
          mappedPlan = rawType;
        }

        setSelectedPlan(mappedPlan);

        // ✅ Also normalize billing cycle if stored (optional)
        const rawCycle = String(data.billing_cycle || "").toLowerCase();
        if (["monthly", "quarterly", "yearly"].includes(rawCycle)) {
          setBillingCycle(rawCycle);
        }
      } else {
        const normalizedContextPlan = currentPlan
          ? String(currentPlan).toLowerCase()
          : "free";
        const fallbackPlan = ["free", "starter", "sme", "business"].includes(
          normalizedContextPlan
        )
          ? normalizedContextPlan
          : "free";
        setSelectedPlan(fallbackPlan);
      }

      setLoading(false);
    };

    fetchBillingInfo();
  }, [companyId, currentPlan]);

  // 🔹 Normalised plan/status + period
  const today = new Date();

  const normalizedBillingType = billingInfo?.billing_type
    ? String(billingInfo.billing_type).toLowerCase()
    : "free";

  const billingStatus = billingInfo?.billing_status || null;

  const periodStart = useMemo(() => {
    if (!billingInfo?.created_at) return null;
    return new Date(billingInfo.created_at);
  }, [billingInfo]);

  const periodEnd = useMemo(() => {
    if (!billingInfo?.next_billing_date) return null;
    return new Date(billingInfo.next_billing_date);
  }, [billingInfo]);

  const isNonFreePlan = normalizedBillingType !== "free";
  const subscriptionNotExpired =
    periodEnd && periodEnd.getTime() >= today.getTime();

  // Treat both active + trial as "active subscription" for locking & notifications
  const hasActiveSubscription =
    isNonFreePlan &&
    subscriptionNotExpired &&
    (billingStatus === "active" || billingStatus === "trial");

  const activePlan = hasActiveSubscription ? normalizedBillingType : null;

  const daysRemaining =
    periodEnd != null
      ? Math.ceil(
          (periodEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        )
      : null;

  // 🔹 Once-per-period popup for expiring subscriptions (within 7 days)
  useEffect(() => {
    if (
      !companyId ||
      !hasActiveSubscription ||
      !periodEnd ||
      daysRemaining == null
    )
      return;
    if (daysRemaining > 7 || daysRemaining < 0) return;

    const periodKey = periodEnd.toISOString().slice(0, 10);
    const key = `kp_billing_notice_${companyId}_${periodKey}`;
    if (!window.localStorage.getItem(key)) {
      setShowExpiryPopup(true);
      window.localStorage.setItem(key, "shown");
    }
  }, [companyId, hasActiveSubscription, periodEnd, daysRemaining]);

  // 🔹 Helper: is this particular plan locked (cannot re-buy while active/trial)?
  const isPlanLocked = (planName) =>
    hasActiveSubscription &&
    activePlan === planName &&
    periodEnd &&
    periodEnd.getTime() >= today.getTime();

  const isCurrentPlanLocked = isPlanLocked(selectedPlan);

  // ✅ Helper: plan supports a billing cycle
  const supportsCycle = (planKey, cycle) => {
    const cfg = planConfig[planKey];
    if (!cfg) return false;

    if (cycle === "monthly") return cfg.monthly != null;
    if (cycle === "quarterly") return cfg.quarterly != null;
    if (cycle === "yearly") return cfg.yearly != null;

    return false;
  };

  // ✅ If user selects SME while on monthly, auto-switch to quarterly
  useEffect(() => {
    if (selectedPlan === "sme" && billingCycle === "monthly") {
      setBillingCycle("quarterly");
    }
  }, [selectedPlan, billingCycle]);

  // 2) Handle plan selection (UI only – write happens on submit)
  const handlePlanChange = (e) => {
    const newPlan = e.target.value;
    setSelectedPlan(newPlan);

    // If new plan doesn't support the current cycle, move to the first valid cycle
    if (!supportsCycle(newPlan, billingCycle)) {
      if (supportsCycle(newPlan, "monthly")) setBillingCycle("monthly");
      else if (supportsCycle(newPlan, "quarterly"))
        setBillingCycle("quarterly");
      else setBillingCycle("yearly");
    }
  };

  // 3) Billing + Paystack handling
  const handleCompletePayment = async (e) => {
    e.preventDefault();

    if (!acceptTerms) {
      alert("You must agree to the Terms of Use.");
      return;
    }

    if (!companyId) {
      setError("No company selected.");
      return;
    }

    if (!userId) {
      setError("No user session found. Please log in again.");
      return;
    }

    if (!cardDetails.email) {
      alert("Please enter your email.");
      return;
    }

    // 🚫 Block paying again for the same plan while it's active or on trial
    if (isCurrentPlanLocked) {
      alert(
        `You already have an active ${
          activePlan?.toUpperCase() || ""
        } subscription for this period. You can upgrade, downgrade to Free, or wait until it expires.`
      );
      return;
    }

    // 🚫 Block unsupported cycles (e.g. SME monthly)
    if (!supportsCycle(selectedPlan, billingCycle)) {
      alert(
        `${planConfig[selectedPlan]?.name || "This plan"} does not support ${
          billingCycle === "yearly"
            ? "Yearly"
            : billingCycle === "quarterly"
            ? "Quarterly"
            : "Monthly"
        } billing. Please select another billing option.`
      );
      return;
    }

    setSubmitting(true);
    setError(null);

    const cfg = planConfig[selectedPlan] || planConfig.free;

    try {
      // 🔹 Case 1: 3-month free SME trial (no immediate charge) — replaces Pro trial
      if (selectedPlan === "business" && trialEligible) {
        const threeMonthsFromNow = new Date();
        threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

        const payload = {
          company_id: companyId,
          user_id: userId,
          billing_type: "business",
          billing_status: "trial",
          billing_cycle: "trial",
          next_billing_date: threeMonthsFromNow.toISOString().slice(0, 10), // YYYY-MM-DD
          payment_method: "paystack_trial",
          updated_at: new Date().toISOString(),
        };

        const { error: upsertError } = await supabase
          .from("billing")
          .upsert(payload);

        if (upsertError) {
          console.error("Error starting SME trial:", upsertError);
          throw upsertError;
        }

        setBillingInfo((prev) => ({ ...(prev || {}), ...payload }));
        if (typeof refreshCompanyContext === "function") {
          await refreshCompanyContext();
        }

        alert(
          "Your 3-month free Business trial is now active! You’ll be billed only after the trial ends."
        );
      } else if (selectedPlan === "free") {
        // 🔹 Case 2: Explicitly activate Free plan (no Paystack call)
        const payload = {
          company_id: companyId,
          user_id: userId,
          billing_type: "free",
          billing_status: "active",
          billing_cycle: "free",
          next_billing_date: null,
          payment_method: "none",
          updated_at: new Date().toISOString(),
        };

        const { error: freeError } = await supabase
          .from("billing")
          .upsert(payload);

        if (freeError) {
          console.error("Error activating Free plan:", freeError);
          throw freeError;
        }

        setBillingInfo((prev) => ({ ...(prev || {}), ...payload }));
        if (typeof refreshCompanyContext === "function") {
          await refreshCompanyContext();
        }

        alert("You’re now on the Free plan.");
      } else {
        // ✅ Case 3: Paid plans — uses Supabase Edge Function
        const amountToPay =
          billingCycle === "yearly"
            ? cfg.yearly
            : billingCycle === "quarterly"
            ? cfg.quarterly
            : cfg.monthly;

        const { data, error: fnError } = await supabase.functions.invoke(
          "paystack-initialize",
          {
            body: {
              amount: amountToPay,
              email: cardDetails.email,
              plan: selectedPlan,
              companyId,
              userId,
              billingCycle, // monthly | quarterly | yearly
            },
          }
        );

        if (fnError) {
          console.error("paystack-initialize function error:", fnError);

          const ctx = fnError?.context;
          const status = ctx?.status;
          const bodyStream = ctx?.body;

          let bodyText = "";
          try {
            if (bodyStream && typeof bodyStream.getReader === "function") {
              const reader = bodyStream.getReader();
              const chunks = [];
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
              }
              bodyText = new TextDecoder().decode(
                chunks.length === 1 ? chunks[0] : concatUint8(chunks)
              );
            }
          } catch (e) {
            console.warn("Could not read function error body stream:", e);
          }

          console.error("Function error status:", status);
          console.error("Function error bodyText:", bodyText);

          let parsed;
          try {
            parsed = bodyText ? JSON.parse(bodyText) : null;
          } catch {
            parsed = null;
          }

          throw new Error(
            parsed?.error ||
              parsed?.message ||
              fnError.message ||
              "Unable to initialize payment."
          );
        }

        // helper
        function concatUint8(chunks) {
          const total = chunks.reduce((sum, c) => sum + c.length, 0);
          const out = new Uint8Array(total);
          let offset = 0;
          for (const c of chunks) {
            out.set(c, offset);
            offset += c.length;
          }
          return out;
        }

        if (!data?.authorization_url) {
          console.error("paystack-initialize bad response:", data);
          throw new Error("Missing authorization_url from billing init.");
        }

        // Redirect user to Paystack's hosted payment page
        window.location.href = data.authorization_url;
        return; // no further local work
      }
    } catch (err) {
      console.error("Error completing billing:", err);
      setError("Unable to update billing at the moment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    setCardDetails((prev) => ({ ...prev, [name]: value }));
  };

  const cyclePrice = useMemo(() => {
    const cfg = planConfig[selectedPlan] || planConfig.free;

    if (billingCycle === "yearly") return cfg.yearly ?? 0;
    if (billingCycle === "quarterly") return cfg.quarterly ?? 0;
    return cfg.monthly ?? 0;
  }, [selectedPlan, billingCycle, planConfig]);

  const plan = planConfig[selectedPlan] || planConfig.free;

  const formatDate = (d) =>
    d
      ? d.toLocaleDateString("en-NG", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "—";

  // ✅ Friendly label for current plan display (handles legacy "pro" -> "sme")
  const displayCurrentPlan = useMemo(() => {
    const raw = (currentPlan || "free").toLowerCase();
    if (raw === "pro") return "sme";
    if (raw === "premium") return "sme";
    if (raw === "basic") return "starter";
    return raw;
  }, [currentPlan]);

  if (loading) {
    return (
      <div className="billing-page loading-state">
        <div className="spinner" />
        <p>Loading billing information…</p>
      </div>
    );
  }
  if (error) return <p className="error">{error}</p>;

  return (
    <div className="billing-page">
      {/* Expiry popup (within 7 days) */}
      {showExpiryPopup && hasActiveSubscription && (
        <div
          className="billing-popup-overlay"
          onClick={() => setShowExpiryPopup(false)}
        >
          <div className="billing-popup" onClick={(e) => e.stopPropagation()}>
            <h3>Subscription expiring soon</h3>
            <p>
              Your <strong>{activePlan?.toUpperCase()}</strong> subscription is
              ending on <strong>{formatDate(periodEnd)}</strong>.
            </p>
            {daysRemaining != null && daysRemaining >= 0 && (
              <p>
                You have <strong>{daysRemaining}</strong> day
                {daysRemaining === 1 ? "" : "s"} remaining.
              </p>
            )}
            <button
              className="btn-primary"
              onClick={() => setShowExpiryPopup(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <header className="billing-header">
        <div className="title-side">
          <h1>Billing & Subscription</h1>
          <p className="muted">
            Securely manage your plan and subscription via Paystack.
          </p>
          <p className="muted small">
            Current plan:&nbsp;
            <strong>
              {String(displayCurrentPlan || "free").toUpperCase()}
            </strong>
            {isOnTrial && (
              <span className="badge trial">On Business Trial</span>
            )}
          </p>

          {/* Show period for any non-free active/trial subscription */}
          {isNonFreePlan && periodStart && periodEnd && (
            <p className="muted small">
              Subscription period:&nbsp;
              <strong>
                {formatDate(periodStart)} – {formatDate(periodEnd)}
              </strong>{" "}
              {daysRemaining != null && daysRemaining >= 0 && (
                <span className="badge small-badge">
                  {daysRemaining} day
                  {daysRemaining === 1 ? "" : "s"} left
                </span>
              )}
            </p>
          )}
        </div>
        <div className="actions-side">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => window.location.reload()}
          >
            <RefreshCcw size={16} /> Refresh
          </button>
        </div>
      </header>

      <div className="billing-steps">
        <div className="step active">
          <span className="step-number">1</span> Billing Details
        </div>
        <div className="step">
          <span className="step-number">2</span> Pay with Paystack
        </div>
      </div>

      <div className="billing-grid">
        {/* LEFT: Form */}
        <section className="panel form-panel">
          <form onSubmit={handleCompletePayment} className="billing-form">
            <div className="section-heading">
              <Building2 size={18} />
              <h3>Billing Details</h3>
            </div>

            <div className="two-cols">
              <div className="input-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={cardDetails.firstName}
                  onChange={handleDetailsChange}
                  required
                />
              </div>
              <div className="input-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={cardDetails.lastName}
                  onChange={handleDetailsChange}
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <label>Address</label>
              <input
                type="text"
                name="address"
                value={cardDetails.address}
                onChange={handleDetailsChange}
                required
              />
            </div>

            <div className="two-cols">
              <div className="input-group">
                <label>
                  <Mail size={14} /> Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={cardDetails.email}
                  onChange={handleDetailsChange}
                  required
                />
              </div>
              <div className="input-group">
                <label>
                  <Phone size={14} /> Phone
                </label>
                <input
                  type="text"
                  name="phone"
                  value={cardDetails.phone}
                  onChange={handleDetailsChange}
                  required
                />
              </div>
            </div>

            <div className="terms-section">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={() => setAcceptTerms((p) => !p)}
                />
                <span>
                  I agree to the <a href="#!">Terms of Use</a> &{" "}
                  <a href="#!">Privacy Policy</a>.
                </span>
              </label>
            </div>

            {isCurrentPlanLocked && (
              <p className="muted small warning-text">
                You already have an active{" "}
                <strong>{activePlan?.toUpperCase()}</strong> subscription for
                this period. To change your plan, choose a different option
                (Free or upgrade/downgrade).
              </p>
            )}

            {/* ✅ SME monthly restriction notice */}
            {selectedPlan === "sme" && billingCycle === "monthly" && (
              <p className="muted small warning-text">
                SME plan does not support monthly payment. Please choose
                <strong> Quarterly</strong> or <strong>Yearly</strong>.
              </p>
            )}

            <button
              type="submit"
              className="btn-primary wide"
              disabled={submitting || isCurrentPlanLocked}
            >
              {submitting
                ? "Processing…"
                : selectedPlan === "business" && trialEligible
                ? "Start 3-month Free Business Trial"
                : selectedPlan === "free"
                ? "Activate Free Plan"
                : `Continue to Paystack (₦${cyclePrice.toLocaleString()})`}{" "}
              <ArrowRight size={16} />
            </button>

            <p className="secure-note">
              <ShieldCheck size={16} /> All payments are handled securely via
              Paystack. We don’t store your card details.
            </p>
          </form>
        </section>

        {/* RIGHT: Plan summary */}
        <aside className="panel summary-panel">
          <div className="summary-head">
            <div className="kicker">Plan</div>
            <h2>{plan.name}</h2>
          </div>

          {/* ✅ Cycle picker now includes Quarterly (no discount) */}
          <div className="cycle-picker">
            <button
              type="button"
              className={`cycle ${billingCycle === "monthly" ? "active" : ""}`}
              onClick={() => setBillingCycle("monthly")}
              disabled={!supportsCycle(selectedPlan, "monthly")}
              title={
                supportsCycle(selectedPlan, "monthly")
                  ? ""
                  : "This plan does not support monthly payment"
              }
            >
              Monthly
            </button>

            <button
              type="button"
              className={`cycle ${
                billingCycle === "quarterly" ? "active" : ""
              }`}
              onClick={() => setBillingCycle("quarterly")}
              disabled={!supportsCycle(selectedPlan, "quarterly")}
              title={
                supportsCycle(selectedPlan, "quarterly")
                  ? ""
                  : "This plan does not support quarterly payment"
              }
            >
              Quarterly
            </button>

            <button
              type="button"
              className={`cycle ${billingCycle === "yearly" ? "active" : ""}`}
              onClick={() => setBillingCycle("yearly")}
              disabled={!supportsCycle(selectedPlan, "yearly")}
              title={
                supportsCycle(selectedPlan, "yearly")
                  ? ""
                  : "This plan does not support yearly payment"
              }
            >
              Yearly
              <span className="badge">Save more</span>
            </button>
          </div>

          <div className="price">
            {billingCycle === "yearly" ? (
              <>
                <span className="major">
                  ₦{(plan.yearly ?? 0).toLocaleString()}
                </span>
                <span className="per">/year</span>
              </>
            ) : billingCycle === "quarterly" ? (
              <>
                <span className="major">
                  ₦{(plan.quarterly ?? 0).toLocaleString()}
                </span>
                <span className="per">/quarter</span>
              </>
            ) : (
              <>
                <span className="major">
                  ₦{(plan.monthly ?? 0).toLocaleString()}
                </span>
                <span className="per">/month</span>
              </>
            )}
          </div>

          {selectedPlan === "business" && trialEligible && (
            <p className="muted small highlight">
              🎁 First-time users get <strong>3 months of Business free</strong>
              . You’ll only be charged after your trial ends.
            </p>
          )}

          {/* ✅ SME billing rule reminder */}
          {selectedPlan === "sme" && (
            <p className="muted small">
              ℹ️ SME plan supports <strong>Quarterly</strong> and{" "}
              <strong>Yearly</strong> payment only.
            </p>
          )}

          <div className="select-wrap">
            <label htmlFor="planSelect" className="select-label">
              Select a plan
            </label>
            <select
              id="planSelect"
              className="select"
              value={selectedPlan}
              onChange={handlePlanChange}
            >
              <option value="free">Free</option>
              <option value="starter" disabled={isPlanLocked("starter")}>
                Starter
                {activePlan === "starter" ? " (current)" : ""}
              </option>
              <option value="sme" disabled={isPlanLocked("sme")}>
                SME
                {activePlan === "sme" ? " (current)" : ""}
              </option>
              <option value="business" disabled={isPlanLocked("business")}>
                Business
                {activePlan === "business" ? " (current)" : ""}
              </option>
            </select>
          </div>

          <ul className="perks">
            {plan.perks.map((p) => (
              <li key={p}>
                <Check size={16} /> {p}
              </li>
            ))}
          </ul>

          <div className="divider" />

          <div className="mini-cards">
            <div className="mini-card">
              <Receipt size={16} />
              <div>
                <strong>Invoices & Receipts</strong>
                <p className="muted small">
                  Download PDFs & keep branded records.
                </p>
              </div>
            </div>
            <div className="mini-card">
              <ShieldCheck size={16} />
              <div>
                <strong>RLS-secured</strong>
                <p className="muted small">
                  Your billing data is scoped to your company.
                </p>
              </div>
            </div>
          </div>

          <div className="policy">
            <div className="flag success">
              <CheckCircle2 size={16} />
              <span>7-day money-back guarantee</span>
            </div>
            <div className="flag danger">
              <XCircle size={16} />
              <span>Auto-renew can be disabled anytime</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BillingPage;
